import React, { useState, useCallback, useEffect } from 'react';
import { Upload, FileText, X, CheckCircle, AlertCircle, Brain, Eye, ChevronDown, ChevronRight } from 'lucide-react';
import { uploadFile, analyzeFactSheet, getAnalysis, createProject, associateFactSheetWithProject, deleteFactSheet, getAuthenticatedFileUrl, VWBAAnalysis, matchProject, Project } from '../services/api';
import VWBALoader from './VWBALoader';
import FactsheetComparisonModal from './FactsheetComparisonModal';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  status: 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  factSheetId?: string;
  projectName?: string;
  analysisStatus?: 'pending' | 'analyzing' | 'completed' | 'error';
  vwbaAnalysis?: VWBAAnalysis;
  rawResponse?: string;
  filename?: string; // The actual filename stored on disk
  authenticatedUrl?: string; // Authenticated blob URL for file access
}

const MAX_UPLOAD_SIZE_MB = 4;
const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

const UploadPage: React.FC = () => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [viewMode, setViewMode] = useState<'structured' | 'raw'>('structured');
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [showDetails, setShowDetails] = useState<Record<string, boolean>>({});
  const [showLoader, setShowLoader] = useState(false);
  const [comparisonModal, setComparisonModal] = useState<{
    isOpen: boolean;
    extractedData: VWBAAnalysis | null;
    existingProject: Project | null;
    factSheetId: string;
    fileId: string;
  } | null>(null);

  // Cleanup blob URLs on component unmount
  useEffect(() => {
    return () => {
      uploadedFiles.forEach(file => {
        if (file.authenticatedUrl) {
          URL.revokeObjectURL(file.authenticatedUrl);
        }
      });
    };
  }, []);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    const validFiles = fileArray.filter(file => file.size <= MAX_UPLOAD_SIZE_BYTES);
    
    if (validFiles.length === 0) {
      alert(`No valid files to upload. Maximum file size is ${MAX_UPLOAD_SIZE_MB}MB.`);
      return;
    }

    // Create initial file states
    const newFiles: UploadedFile[] = validFiles.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      name: file.name,
      size: file.size,
      status: 'uploading',
      progress: 0,
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    // Upload files
    for (const file of validFiles) {
      try {
        const fileId = newFiles.find(f => f.name === file.name)?.id;
        if (!fileId) continue;

        // Update progress
        setUploadedFiles(prev => 
          prev.map(f => f.id === fileId ? { ...f, progress: 50 } : f)
        );

        const result = await uploadFile(file);
        
        if (result.success && result.factSheet) {
          // Generate authenticated URL for the file
          let authenticatedUrl: string | undefined;
          try {
            authenticatedUrl = await getAuthenticatedFileUrl(result.factSheet.filename);
          } catch (error) {
            console.error('Failed to generate authenticated URL:', error);
          }
          
          setUploadedFiles(prev => 
            prev.map(f => f.id === fileId ? { 
              ...f, 
              status: 'success', 
              progress: 100,
              factSheetId: result.factSheet!.id,
              analysisStatus: 'pending',
              filename: result.factSheet!.filename,
              authenticatedUrl
            } : f)
          );
          
          // Automatically start analysis after successful upload
          if (result.factSheet.id) {
            startAnalysis(result.factSheet.id, fileId);
          }
        } else {
          setUploadedFiles(prev => 
            prev.map(f => f.id === fileId ? { 
              ...f, 
              status: 'error', 
              error: result.error || 'Upload failed'
            } : f)
          );
        }
      } catch (error) {
        const fileId = newFiles.find(f => f.name === file.name)?.id;
        if (fileId) {
          setUploadedFiles(prev => 
            prev.map(f => f.id === fileId ? { 
              ...f, 
              status: 'error', 
              error: error instanceof Error ? error.message : 'Upload failed'
            } : f)
          );
        }
      }
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  }, [handleFiles]);

  const startAnalysis = async (factSheetId: string, fileId: string) => {
    try {
      setShowLoader(true);

      setUploadedFiles(prev => 
        prev.map(f => f.id === fileId ? { 
          ...f, 
          analysisStatus: 'analyzing' 
        } : f)
      );

      // Start the analysis (this will return immediately)
      const result = await analyzeFactSheet(factSheetId);
      
      if (result.success) {
        // Start polling for results
        pollAnalysisStatus(factSheetId, fileId);
      } else {
        setShowLoader(false);
        setUploadedFiles(prev => 
          prev.map(f => f.id === fileId ? { 
            ...f, 
            analysisStatus: 'error',
            error: result.error || 'Analysis failed'
          } : f)
        );
      }
    } catch (error) {
      setShowLoader(false);
      setUploadedFiles(prev => 
        prev.map(f => f.id === fileId ? { 
          ...f, 
          analysisStatus: 'error',
          error: error instanceof Error ? error.message : 'Analysis failed'
        } : f)
      );
    }
  };

  const pollAnalysisStatus = async (factSheetId: string, fileId: string) => {
    const pollInterval = 2000; // Poll every 2 seconds
    const maxAttempts = 150; // Max 5 minutes of polling
    let attempts = 0;

    const poll = async () => {
      try {
        attempts++;
        const result = await getAnalysis(factSheetId);
        
        if (result.success && result.analysis) {
          const analysis = result.analysis;
          
          if (analysis.status === 'COMPLETED') {
            // Parse the results
            const vwbaAnalysis = analysis.results ? JSON.parse(analysis.results) : null;

            // Check for matching project
            if (vwbaAnalysis) {
              const projectName = vwbaAnalysis.projectName || '';
              const projectBasin = vwbaAnalysis.extractedData?.basin || vwbaAnalysis.extractedData?.location || '';

              // Capture factSheetId before any state updates to avoid stale closure
              const currentFile = uploadedFiles.find(f => f.id === fileId);
              const currentFactSheetId = currentFile?.factSheetId || '';

              if (projectName && projectBasin) {
                try {
                  const matchResult = await matchProject(projectName, projectBasin);

                  if (matchResult.match && matchResult.project) {
                    // Found a matching project - show comparison modal
                    setShowLoader(false);
                    setUploadedFiles(prev =>
                      prev.map(f => f.id === fileId ? {
                        ...f,
                        analysisStatus: 'completed',
                        vwbaAnalysis: vwbaAnalysis,
                        rawResponse: analysis.rawResponse
                      } : f)
                    );

                    // Use captured factSheetId to avoid stale closure
                    if (currentFactSheetId) {
                      setComparisonModal({
                        isOpen: true,
                        extractedData: vwbaAnalysis,
                        existingProject: matchResult.project,
                        factSheetId: currentFactSheetId,
                        fileId: fileId,
                      });
                    }
                    return; // Stop polling
                  }
                } catch (matchError) {
                  console.error('Error checking for matching project:', matchError);
                  // Continue with normal flow if matching fails
                }
              }
            }

            // No match found - continue with normal flow
            setShowLoader(false);
            setUploadedFiles(prev =>
              prev.map(f => f.id === fileId ? {
                ...f,
                analysisStatus: 'completed',
                vwbaAnalysis: vwbaAnalysis,
                rawResponse: analysis.rawResponse
              } : f)
            );
            return; // Stop polling
          } else if (analysis.status === 'ERROR') {
            setShowLoader(false);
            setUploadedFiles(prev => 
              prev.map(f => f.id === fileId ? { 
                ...f, 
                analysisStatus: 'error',
                error: 'Analysis failed'
              } : f)
            );
            return; // Stop polling
          } else if (attempts < maxAttempts) {
            // Continue polling
            setTimeout(poll, pollInterval);
          } else {
            // Timeout
            setShowLoader(false);
            setUploadedFiles(prev => 
              prev.map(f => f.id === fileId ? { 
                ...f, 
                analysisStatus: 'error',
                error: 'Analysis timed out'
              } : f)
            );
          }
        } else if (attempts < maxAttempts) {
          // Continue polling
          setTimeout(poll, pollInterval);
        } else {
          // Timeout
          setShowLoader(false);
          setUploadedFiles(prev => 
            prev.map(f => f.id === fileId ? { 
              ...f, 
              analysisStatus: 'error',
              error: 'Analysis timed out'
            } : f)
          );
        }
      } catch (error) {
        if (attempts < maxAttempts) {
          // Continue polling on error
          setTimeout(poll, pollInterval);
        } else {
          setShowLoader(false);
          setUploadedFiles(prev => 
            prev.map(f => f.id === fileId ? { 
              ...f, 
              analysisStatus: 'error',
              error: error instanceof Error ? error.message : 'Analysis failed'
            } : f)
          );
        }
      }
    };

    // Start polling
    setTimeout(poll, pollInterval);
  };

  const removeFile = async (fileId: string) => {
    try {
      // Find the file to get its factSheetId and authenticatedUrl
      const file = uploadedFiles.find(f => f.id === fileId);
      if (file) {
        // Clean up blob URL to prevent memory leaks
        if (file.authenticatedUrl) {
          URL.revokeObjectURL(file.authenticatedUrl);
        }
        
        if (file.factSheetId) {
          // Delete the fact sheet from the backend
          await deleteFactSheet(file.factSheetId);
        }
      }
      
      // Remove the file from the uploaded files list
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (error) {
      console.error('Error deleting file:', error);
      // Still remove from UI even if backend deletion fails
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
      alert('File removed from upload list, but there may have been an error deleting it from the server.');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const isSectionCollapsed = (sectionId: string) => {
    return collapsedSections[sectionId] || false;
  };

  const toggleDetails = (fileId: string) => {
    setShowDetails(prev => ({
      ...prev,
      [fileId]: !prev[fileId]
    }));
  };

  const isDetailsShown = (fileId: string) => {
    return showDetails[fileId] || false;
  };

  const saveProject = async (analysis: VWBAAnalysis, fileName: string, fileId: string) => {
    try {
      const projectName = analysis.projectName || `Project from ${fileName}`;
      const projectDescription = analysis.projectDescription || `Analysis results from ${fileName}`;

      // Create the project
      const project = await createProject({ name: projectName, description: projectDescription });

      // Find the file to get its factSheetId
      const file = uploadedFiles.find(f => f.id === fileId);
      if (file && file.factSheetId) {
        // Associate the fact sheet with the project
        await associateFactSheetWithProject(file.factSheetId, project.id);
      }

      // Remove the file from the uploaded files list after successful save
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));

      // Show success message (you could add a toast notification here)
      alert('Project saved successfully! It has been moved to the Projects page.');
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Failed to save project. Please try again.');
    }
  };

  const handleComparisonResolved = () => {
    if (comparisonModal) {
      // Remove the file from the upload list after resolution
      setUploadedFiles(prev => prev.filter(f => f.id !== comparisonModal.fileId));
      setComparisonModal(null);
      alert('Project updated successfully!');
    }
  };

  return (
    <div className="h-full w-full px-4 py-4 space-y-4">
      {/* VWBA Analysis Loader */}
      <VWBALoader
        isVisible={showLoader}
        onClose={() => {
          setShowLoader(false);
        }}
      />

      {/* Factsheet Comparison Modal */}
      {comparisonModal?.isOpen && comparisonModal.extractedData && comparisonModal.existingProject && (
        <FactsheetComparisonModal
          extractedData={comparisonModal.extractedData}
          existingProject={comparisonModal.existingProject}
          factSheetId={comparisonModal.factSheetId}
          onClose={() => setComparisonModal(null)}
          onResolved={handleComparisonResolved}
        />
      )}

      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Upload VWBA Fact Sheets
        </h2>
        <p className="text-lg text-gray-600">
          Upload PDF fact sheets to analyze and determine the appropriate calculation methodology
        </p>
      </div>


      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          type="file"
          multiple
          accept=".pdf"
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        
        <div className="space-y-4">
          <Upload className="w-12 h-12 text-gray-400 mx-auto" />
          <div>
            <p className="text-lg font-medium text-gray-900">
              Drop your PDF files here, or click to browse
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Supports PDF files up to {MAX_UPLOAD_SIZE_MB}MB
            </p>
          </div>
        </div>
      </div>

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Uploaded Files</h3>
          <div className="space-y-2">
            {uploadedFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-4 bg-white rounded-lg border"
              >
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {file.status === 'uploading' && (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm text-blue-600">{file.progress}%</span>
                    </div>
                  )}
                  
                  {file.status === 'success' && (
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-2 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-sm">Uploaded</span>
                      </div>
                      
                      {file.analysisStatus === 'pending' && (
                        <div className="flex items-center space-x-2 text-yellow-600">
                          <Brain className="w-4 h-4" />
                          <span className="text-sm">Queued for analysis</span>
                        </div>
                      )}
                      
                      {file.analysisStatus === 'analyzing' && (
                        <div className="flex items-center space-x-2 text-blue-600">
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm">Analyzing...</span>
                        </div>
                      )}
                      
                      {file.analysisStatus === 'completed' && (
                        <div className="flex items-center space-x-2 text-green-600">
                          <Brain className="w-4 h-4" />
                          <span className="text-sm">Analysis complete</span>
                        </div>
                      )}
                      
                      {file.analysisStatus === 'error' && (
                        <div className="flex items-center space-x-2 text-red-600">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-sm">Analysis failed</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {file.status === 'error' && (
                    <div className="flex items-center space-x-2 text-red-600">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm">{file.error}</span>
                    </div>
                  )}
                  
                  <button
                    onClick={() => removeFile(file.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {uploadedFiles.some(f => f.analysisStatus === 'completed' && f.vwbaAnalysis) && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Analysis Results</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('structured')}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  viewMode === 'structured'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Structured Data
              </button>
              <button
                onClick={() => setViewMode('raw')}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  viewMode === 'raw'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Raw Response
              </button>
            </div>
          </div>
          {uploadedFiles
            .filter(f => f.analysisStatus === 'completed' && f.vwbaAnalysis)
            .map((file) => (
              <div key={file.id} className="bg-white rounded-lg border">
                {/* PDF Preview and Analysis Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
                  {/* Left Column - PDF Preview */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-md font-medium text-gray-900">PDF Preview</h4>
                      <span className="text-sm text-gray-500">{file.name}</span>
                    </div>
                    <div className="border rounded-lg overflow-hidden" style={{ height: '800px' }}>
                      {file.authenticatedUrl ? (
                        <iframe
                          src={file.authenticatedUrl}
                          className="w-full h-full"
                          title={`PDF Preview: ${file.name}`}
                          style={{ border: 'none' }}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full bg-gray-100">
                          <div className="text-center">
                            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500">Loading PDF preview...</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Right Column - Analysis Data */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-md font-medium text-gray-900">Analysis Data</h4>
                      <button 
                        onClick={() => toggleDetails(file.id)}
                        className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="text-sm">
                          {isDetailsShown(file.id) ? 'Hide Details' : 'View Details'}
                        </span>
                      </button>
                    </div>
                
                {/* Compact Summary View - shown by default */}
                {viewMode === 'structured' && file.vwbaAnalysis && !isDetailsShown(file.id) && (
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-4 border border-gray-200">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Left Column - Project & Methodology */}
                      <div className="space-y-3">
                        {/* Project Info */}
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <h6 className="font-semibold text-gray-800 text-sm flex items-center">
                              <FileText className="w-4 h-4 mr-1" />
                              Project
                            </h6>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {file.vwbaAnalysis.confidence}% confidence
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-900 mb-1">Project Analysis</p>
                          <p className="text-xs text-gray-600 line-clamp-2">Analysis results from uploaded document</p>
                        </div>

                        {/* Recommended Methodology */}
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <h6 className="font-semibold text-gray-800 text-sm flex items-center">
                              <Brain className="w-4 h-4 mr-1 text-green-600" />
                              Recommended Method
                            </h6>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {file.vwbaAnalysis.recommendedMethodology.confidence}%
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-900 mb-1">{file.vwbaAnalysis.recommendedMethodology.name}</p>
                          <p className="text-xs text-gray-600 line-clamp-2">{file.vwbaAnalysis.recommendedMethodology.description}</p>
                        </div>
                      </div>

                      {/* Right Column - Inputs Status */}
                      <div className="space-y-3">
                        {/* Inputs Overview */}
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <h6 className="font-semibold text-gray-800 text-sm mb-2 flex items-center">
                            <CheckCircle className="w-4 h-4 mr-1 text-blue-600" />
                            Inputs Status
                          </h6>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Available:</span>
                              <span className="font-medium text-green-600">
                                {file.vwbaAnalysis.availableInputs.length}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Missing:</span>
                              <span className="font-medium text-red-600">
                                {file.vwbaAnalysis.missingInputs.length}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Required:</span>
                              <span className="font-medium text-gray-800">
                                {file.vwbaAnalysis.recommendedMethodology.inputs.filter(i => i.required).length}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Optional:</span>
                              <span className="font-medium text-gray-600">
                                {file.vwbaAnalysis.recommendedMethodology.inputs.filter(i => !i.required).length}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Missing Inputs Quick View */}
                        {file.vwbaAnalysis.missingInputs.length > 0 && (
                          <div className="bg-white rounded-lg p-3 border border-gray-200">
                            <h6 className="font-semibold text-gray-800 text-sm mb-2 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1 text-red-600" />
                              Missing Inputs
                            </h6>
                            <div className="space-y-1">
                              {file.vwbaAnalysis.missingInputs.slice(0, 3).map((input, index) => (
                                <div key={index} className="flex items-center justify-between text-xs">
                                  <span className="text-gray-700 truncate">{input.name}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                                    input.required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {input.required ? 'Required' : 'Optional'}
                                  </span>
                                </div>
                              ))}
                              {file.vwbaAnalysis.missingInputs.length > 3 && (
                                <p className="text-xs text-gray-500 text-center">
                                  +{file.vwbaAnalysis.missingInputs.length - 3} more
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Alternative Methodologies Quick View */}
                        {file.vwbaAnalysis.alternativeMethodologies && file.vwbaAnalysis.alternativeMethodologies.length > 0 && (
                          <div className="bg-white rounded-lg p-3 border border-gray-200">
                            <h6 className="font-semibold text-gray-800 text-sm mb-2 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1 text-yellow-600" />
                              Alternatives ({file.vwbaAnalysis.alternativeMethodologies.length})
                            </h6>
                            <div className="space-y-1">
                              {file.vwbaAnalysis.alternativeMethodologies.slice(0, 2).map((methodology, index) => (
                                <div key={index} className="flex items-center justify-between text-xs">
                                  <span className="text-gray-700 truncate">{methodology.name}</span>
                                  <span className="px-1.5 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">
                                    {methodology.confidence}%
                                  </span>
                                </div>
                              ))}
                              {file.vwbaAnalysis.alternativeMethodologies.length > 2 && (
                                <p className="text-xs text-gray-500 text-center">
                                  +{file.vwbaAnalysis.alternativeMethodologies.length - 2} more
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Save Project Button */}
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <button
                        onClick={() => saveProject(file.vwbaAnalysis!, file.name, file.id)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                      >
                        <FileText className="w-4 h-4" />
                        <span>Save Project</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Detailed View - shown when View Details is clicked */}
                {viewMode === 'structured' && file.vwbaAnalysis && isDetailsShown(file.id) && (
                  <div className="space-y-6">
                    {/* Project Information */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <button
                        onClick={() => toggleSection(`${file.id}-project-info`)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h5 className="font-semibold text-gray-800 flex items-center">
                          <FileText className="w-5 h-5 mr-2" />
                          Project Information
                        </h5>
                        {isSectionCollapsed(`${file.id}-project-info`) ? (
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                      {!isSectionCollapsed(`${file.id}-project-info`) && (
                      <div className="space-y-2">
                        <div>
                          <span className="font-medium text-gray-700">Analysis Type:</span>
                          <p className="text-sm text-gray-600 mt-1">VWBA Project Analysis</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Description:</span>
                          <p className="text-sm text-gray-600 mt-1">Analysis results from uploaded document</p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Overall Confidence: {file.vwbaAnalysis.confidence}%
                          </span>
                        </div>
                      </div>
                      )}
                    </div>

                    {/* Recommended Methodology */}
                    <div className="bg-green-50 rounded-lg p-4">
                      <button
                        onClick={() => toggleSection(`${file.id}-recommended-methodology`)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h5 className="font-semibold text-gray-800 flex items-center">
                          <Brain className="w-5 h-5 mr-2 text-green-600" />
                          Recommended Methodology
                        </h5>
                        {isSectionCollapsed(`${file.id}-recommended-methodology`) ? (
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                      {!isSectionCollapsed(`${file.id}-recommended-methodology`) && (
                      <div className="space-y-3">
                        <div>
                          <span className="font-medium text-gray-700">Methodology:</span>
                          <p className="text-sm text-gray-600 mt-1">{file.vwbaAnalysis.recommendedMethodology.name}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Description:</span>
                          <p className="text-sm text-gray-600 mt-1">{file.vwbaAnalysis.recommendedMethodology.description}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Confidence:</span>
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {file.vwbaAnalysis.recommendedMethodology.confidence}%
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Reasoning:</span>
                          <p className="text-sm text-gray-600 mt-1">{file.vwbaAnalysis.recommendedMethodology.reasoning}</p>
                        </div>
                        
                        {/* Methodology Inputs */}
                        <div>
                          <span className="font-medium text-gray-700">Required Inputs:</span>
                          <div className="mt-2 space-y-2">
                            {file.vwbaAnalysis.recommendedMethodology.inputs.map((input, index) => (
                              <div key={index} className="bg-white rounded p-3 border">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm text-gray-700">{input.name}</span>
                                  <div className="flex items-center space-x-2">
                                    {input.available ? (
                                      <CheckCircle className="w-4 h-4 text-green-500" />
                                    ) : (
                                      <AlertCircle className="w-4 h-4 text-red-500" />
                                    )}
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      input.required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {input.required ? 'Required' : 'Optional'}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{input.description}</p>
                                {input.value && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    <span className="font-medium">Value:</span> {input.value} {input.unit && `(${input.unit})`}
                                  </p>
                                )}
                                {input.source && (
                                  <p className="text-xs text-gray-400 mt-1">
                                    <span className="font-medium">Source:</span> {input.source}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Calculation Steps */}
                        <div>
                          <span className="font-medium text-gray-700">Calculation Steps:</span>
                          <ol className="mt-2 space-y-1">
                            {file.vwbaAnalysis.recommendedMethodology.calculationSteps.map((step, index) => (
                              <li key={index} className="text-sm text-gray-600 flex">
                                <span className="font-medium mr-2">{index + 1}.</span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>
                        </div>

                        {/* References */}
                        <div>
                          <span className="font-medium text-gray-700">References:</span>
                          <ul className="mt-2 space-y-1">
                            {file.vwbaAnalysis.recommendedMethodology.references.map((ref, index) => (
                              <li key={index} className="text-sm text-gray-600 flex">
                                <span className="mr-2">•</span>
                                <span>{ref}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      )}
                    </div>

                    {/* Alternative Methodologies */}
                    {file.vwbaAnalysis.alternativeMethodologies && file.vwbaAnalysis.alternativeMethodologies.length > 0 && (
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <button
                          onClick={() => toggleSection(`${file.id}-alternative-methodologies`)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h5 className="font-semibold text-gray-800 flex items-center">
                            <AlertCircle className="w-5 h-5 mr-2 text-yellow-600" />
                            Alternative Methodologies
                          </h5>
                          {isSectionCollapsed(`${file.id}-alternative-methodologies`) ? (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {!isSectionCollapsed(`${file.id}-alternative-methodologies`) && (
                        <div className="space-y-4">
                          {file.vwbaAnalysis.alternativeMethodologies.map((methodology, index) => (
                            <div key={index} className="bg-white rounded p-3 border">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-gray-700">{methodology.name}</span>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {methodology.confidence}% confidence
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{methodology.description}</p>
                              <p className="text-xs text-gray-500">{methodology.reasoning}</p>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                    )}

                    {/* Extracted Data */}
                    {file.vwbaAnalysis.extractedData && (
                      <div className="bg-blue-50 rounded-lg p-4">
                        <button
                          onClick={() => toggleSection(`${file.id}-extracted-data`)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h5 className="font-semibold text-gray-800 flex items-center">
                            <FileText className="w-5 h-5 mr-2 text-blue-600" />
                            Extracted Data
                          </h5>
                          {isSectionCollapsed(`${file.id}-extracted-data`) ? (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {!isSectionCollapsed(`${file.id}-extracted-data`) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(file.vwbaAnalysis.extractedData).map(([key, value]) => (
                            <div key={key} className="bg-white rounded p-3 border">
                              <span className="font-medium text-gray-700 capitalize">
                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                              </span>
                              <div className="mt-1">
                                {typeof value === 'object' && value !== null ? (
                                  <div className="space-y-1">
                                    {Object.entries(value).map(([subKey, subValue]) => (
                                      <div key={subKey} className="text-sm">
                                        <span className="font-medium text-gray-600">
                                          {subKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                                        </span>
                                        <span className="text-gray-600 ml-1">
                                          {Array.isArray(subValue) ? subValue.join(', ') : String(subValue)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-600">{String(value)}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                    )}

                    {/* Available and Missing Inputs */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Available Inputs */}
                      <div className="bg-green-50 rounded-lg p-4">
                        <button
                          onClick={() => toggleSection(`${file.id}-available-inputs`)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h5 className="font-semibold text-gray-800 flex items-center">
                            <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                            Available Inputs ({file.vwbaAnalysis.availableInputs.length})
                          </h5>
                          {isSectionCollapsed(`${file.id}-available-inputs`) ? (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {!isSectionCollapsed(`${file.id}-available-inputs`) && (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {file.vwbaAnalysis.availableInputs.map((input, index) => (
                            <div key={index} className="bg-white rounded p-3 border">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm text-gray-700">{input.name}</span>
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{input.description}</p>
                              {input.value && (
                                <p className="text-sm text-gray-600 mt-1">
                                  <span className="font-medium">Value:</span> {input.value} {input.unit && `(${input.unit})`}
                                </p>
                              )}
                              {input.source && (
                                <p className="text-xs text-gray-400 mt-1">
                                  <span className="font-medium">Source:</span> {input.source}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                        )}
                      </div>

                      {/* Missing Inputs */}
                      <div className="bg-red-50 rounded-lg p-4">
                        <button
                          onClick={() => toggleSection(`${file.id}-missing-inputs`)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h5 className="font-semibold text-gray-800 flex items-center">
                            <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
                            Missing Inputs ({file.vwbaAnalysis.missingInputs.length})
                          </h5>
                          {isSectionCollapsed(`${file.id}-missing-inputs`) ? (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {!isSectionCollapsed(`${file.id}-missing-inputs`) && (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {file.vwbaAnalysis.missingInputs.map((input, index) => (
                            <div key={index} className="bg-white rounded p-3 border">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm text-gray-700">{input.name}</span>
                                <div className="flex items-center space-x-2">
                                  <AlertCircle className="w-4 h-4 text-red-500" />
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    input.required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {input.required ? 'Required' : 'Optional'}
                                  </span>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{input.description}</p>
                              {input.unit && (
                                <p className="text-xs text-gray-400 mt-1">
                                  <span className="font-medium">Unit:</span> {input.unit}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                    </div>

                    {/* Processing Notes */}
                    {file.vwbaAnalysis.processingNotes && file.vwbaAnalysis.processingNotes.length > 0 && (
                      <div className="bg-purple-50 rounded-lg p-4">
                        <button
                          onClick={() => toggleSection(`${file.id}-processing-notes`)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h5 className="font-semibold text-gray-800 flex items-center">
                            <Brain className="w-5 h-5 mr-2 text-purple-600" />
                            Processing Notes
                          </h5>
                          {isSectionCollapsed(`${file.id}-processing-notes`) ? (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {!isSectionCollapsed(`${file.id}-processing-notes`) && (
                        <div className="space-y-2">
                          {file.vwbaAnalysis.processingNotes.map((note, index) => (
                            <div key={index} className="bg-white rounded p-3 border">
                              <p className="text-sm text-gray-600">{note}</p>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {viewMode === 'raw' && file.rawResponse && (
                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-700 mb-2">Raw Gemini Response</h5>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
                        {file.rawResponse}
                      </pre>
                    </div>
                  </div>
                )}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default UploadPage;
